import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Input, Popconfirm, Table, Tag } from "antd";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeletePlantContract, usePlantContractList } from "../hooks/usePlantContracts";
import { usePlantContractStore } from "../store/plant-contract.store";
import type { PlantContract, PlantType } from "../types";
import { formatDate, formatPrice } from "../utils/format";
import { PlantContractFormModal } from "./PlantContractFormModal";

const PLANT_TYPE_COLOR: Record<PlantType, string> = {
	DARAXT: "green",
	"KO'CHAT": "lime",
	GUL: "magenta",
};

const PLANT_TYPE_LABEL: Record<PlantType, string> = {
	DARAXT: "Daraxt",
	"KO'CHAT": "Ko'chat",
	GUL: "Gul",
};

interface PlantContractTableProps {
	organizationId?: string;
}

export function PlantContractTable({ organizationId }: PlantContractTableProps) {
	const { page: currentPage, limit: pageSize, setPage, getFilter, setFilters } = useUrlTableState();
	const search = getFilter("search");
	const openEditModal = usePlantContractStore((s) => s.openEditModal);
	const editingItem = usePlantContractStore((s) => s.editingItem);

	const { mutate: deleteContract } = useDeletePlantContract();

	const { data, isLoading } = usePlantContractList({
		organizationId,
		page: currentPage,
		limit: pageSize,
		search,
	});

	const items = data?.data?.items ?? [];
	const total = data?.data?.pagination?.total ?? 0;

	return (
		<>
			<TableToolbar
				filters={
					<Input.Search
						// Remount when the URL value changes so back/forward restores the text
						key={search ?? ""}
						placeholder="Yetkazib beruvchi yoki shartnoma raqami"
						allowClear
						style={{ width: 320 }}
						defaultValue={search}
						onSearch={(value) => setFilters({ search: value || null })}
						onChange={(e) => {
							if (!e.target.value && search) {
								setFilters({ search: null });
							}
						}}
					/>
				}
			/>
			<Table
				loading={isLoading}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{
						key: "supplierName",
						title: "Yetkazib beruvchi",
						dataIndex: "supplierName",
						ellipsis: true,
					},
					{
						key: "plantName",
						title: "O'simlik",
						width: 160,
						render: (_, record: PlantContract) =>
							record.plantName ? (
								<span className="flex items-center gap-2">
									{record.plantName}
									{record.plantType && (
										<Tag color={PLANT_TYPE_COLOR[record.plantType]}>
											{PLANT_TYPE_LABEL[record.plantType]}
										</Tag>
									)}
								</span>
							) : (
								"—"
							),
					},
					{
						key: "contractNumber",
						title: "Shartnoma raqami",
						dataIndex: "contractNumber",
						width: 140,
						render: (v: string | null) => <span className="font-mono">{v ?? "—"}</span>,
					},
					{
						key: "price",
						title: "Narx",
						width: 140,
						align: "right",
						render: (_, record: PlantContract) => (
							<span className="font-mono">{formatPrice(record.price)}</span>
						),
					},
					{
						key: "quantity",
						title: "Soni",
						dataIndex: "quantity",
						width: 90,
						align: "right",
						render: (v: number | null) => <span className="font-mono">{v ?? "—"}</span>,
					},
					{
						key: "startDate",
						title: "Boshlanish",
						width: 120,
						render: (_, record: PlantContract) => (
							<span className="font-mono">{formatDate(record.startDate)}</span>
						),
					},
					{
						key: "endDate",
						title: "Tugash",
						width: 120,
						render: (_, record: PlantContract) => (
							<span className="font-mono">{formatDate(record.endDate)}</span>
						),
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_, record: PlantContract) => {
							const menuItems: MenuProps["items"] = [
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
								{
									key: "delete",
									icon: <DeleteOutlined />,
									danger: true,
									label: (
										<Popconfirm
											title="Shartnomani o'chirishni tasdiqlaysizmi?"
											okText="Ha"
											cancelText="Yo'q"
											onConfirm={() => deleteContract(record.id)}
										>
											<span>O'chirish</span>
										</Popconfirm>
									),
								},
							];

							return (
								<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
									<Button type="text" icon={<MoreOutlined />} size="small" />
								</Dropdown>
							);
						},
					},
				]}
			/>
			{(organizationId || editingItem) && (
				<PlantContractFormModal
					organizationId={organizationId ?? editingItem?.organizationId ?? ""}
				/>
			)}
		</>
	);
}
